/**
 * P13-PROD-SCALE-ORG-REPORTING-BOUNDED-READS-WS1 · R0-SCL-03 — the group directory, read page by page.
 *
 * `GET /api/groups` no longer returns every authorized group in one bare array: it answers ONE bounded page
 * (default 50, at most 200 rows) under the same contract as the agency catalogue,
 * `{ success, data, total, pagination: { page, limit, total, pages, hasMore } }`, ordered by name then id,
 * where `total` is the size of the actor's whole scoped directory. Its consumers need the COMPLETE directory
 * (the groups-management table and its totals, the inventory and agency group pickers, the transfer check),
 * so each one reads it through `fetchAllGroupPages` and never treats a first page as the directory.
 *
 * ALL OR NOTHING. The walk is the shared catalogue walker (`readAllCataloguePages`): the complete set, or a
 * thrown `GroupCatalogueIncompleteError` (no pagination contract, a repeated id, an empty page with more
 * pending, a count that differs from `total`, an exhausted budget). It never returns a prefix.
 */
import {
  readAllCataloguePages,
  sharedClientPointPages,
  type PointPageFetcher,
  type PointPageQuery
} from './pointCatalogue'

export const GROUP_CATALOGUE_PATH = '/groups'

export class GroupCatalogueIncompleteError extends Error {
  readonly code = 'GROUP_CATALOGUE_INCOMPLETE'
  constructor(detail: string) {
    super(`El catálogo de grupos no se pudo leer completo: ${detail}`)
    this.name = 'GroupCatalogueIncompleteError'
  }
}

export async function fetchAllGroupPages<T extends { id?: unknown } = any>(
  fetchPage: PointPageFetcher<T>,
  params: PointPageQuery = {}
): Promise<T[]> {
  return readAllCataloguePages(fetchPage, params, (detail) => new GroupCatalogueIncompleteError(detail), 'el grupo')
}

/** The page fetcher of the group directory for a client on the canonical shared instance (keeps the envelope). */
export function sharedClientGroupPages(
  client: { get: (url: string, config?: any) => Promise<{ data: any }> }
): PointPageFetcher {
  return sharedClientPointPages(client, GROUP_CATALOGUE_PATH)
}
